// Image preparation for a small LED screen, as pure functions on RGBA pixels so they can be
// tested without a browser. Studio wraps them with canvas decoding and encoding.

/** "12_sarah_jones.jpg" → { number: "12", name: "Sarah Jones" }; "mary-kate_o'neill.png" → "Mary-Kate O'Neill". */
export function nameFromFile(filename: string): { name: string; number: string } {
  let base = filename.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/\(\d+\)$/, '').trim();
  let number = '';
  const lead = base.match(/^(\d{1,3})[\s_.-]+(.*)$/);
  if (lead) { number = String(Number(lead[1])); base = lead[2]; }
  const cap = (w: string) => w.split('-').map(part => part.split("'").map(p => p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : p).join("'")).join('-');
  const name = base.split(/[\s_]+/).filter(Boolean).map(cap).join(' ')
    .replace(/\bMc([a-z])/g, (_, c: string) => `Mc${c.toUpperCase()}`);
  return { name: name.slice(0, 40), number };
}

/** A sponsor's name from its logo file: "carrs_billington-logo.png" → "Carrs Billington". */
export function sponsorNameFromFile(filename: string): string {
  return filename.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[-_ ]*(logo|final|hi-?res|rgb|cmyk|web)$/i, '')
    .split(/[\s_-]+/).filter(Boolean)
    .map(w => (/[A-Z]/.test(w) && w === w.toUpperCase() ? w : w[0].toUpperCase() + w.slice(1).toLowerCase()))   // brand capitals stay
    .join(' ').slice(0, 40) || 'Sponsor';
}

export interface Box { x: number; y: number; w: number; h: number }

/**
 * Make a plain white (or already transparent) background transparent, working inward from the
 * edges so white inside the subject (a shirt stripe, teeth) is kept. Optionally clears a club
 * badge in the bottom-left corner, as on the club's photo template. Edits `px` in place and
 * returns the box around what is left, or null if nothing is left.
 */
export function cutOutBackground(px: Uint8ClampedArray, w: number, h: number, o: { threshold?: number; clearBadge?: boolean } = {}): Box | null {
  const thr = o.threshold ?? 228, n = w * h;
  const isBg = (i: number) => px[i * 4 + 3] < 20 || (px[i * 4] > thr && px[i * 4 + 1] > thr && px[i * 4 + 2] > thr);
  const bg = new Uint8Array(n), queue = new Int32Array(n);
  let head = 0, tail = 0;
  const seed = (i: number) => { if (!bg[i] && isBg(i)) { bg[i] = 1; queue[tail++] = i; } };
  for (let x = 0; x < w; x++) { seed(x); seed((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { seed(y * w); seed(y * w + w - 1); }
  while (head < tail) {
    const i = queue[head++], x = i % w;
    if (x > 0) seed(i - 1);
    if (x < w - 1) seed(i + 1);
    if (i >= w) seed(i - w);
    if (i < n - w) seed(i + w);
  }
  if (o.clearBadge) {
    for (let y = Math.floor(h * 0.8); y < h; y++) for (let x = 0; x < Math.floor(w * 0.13); x++) bg[y * w + x] = 1;
  }
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let i = 0; i < n; i++) {
    if (bg[i]) { px[i * 4 + 3] = 0; continue; }
    const x = i % w, y = (i - x) / w;
    // soften the edge by one pixel so cut-outs don't look jagged on the LED
    if ((x > 0 && bg[i - 1]) || (x < w - 1 && bg[i + 1]) || (y > 0 && bg[i - w]) || (y < h - 1 && bg[i + w])) px[i * 4 + 3] = Math.min(px[i * 4 + 3], 150);
    if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** The colour of the top-left corner, used as the background behind a sponsor's logo. */
export function cornerColor(px: Uint8ClampedArray, w: number): string {
  const at = (x: number, y: number) => (y * w + x) * 4;
  if (px[at(2, 2) + 3] < 20) return '#ffffff';              // transparent logo: show it on white
  let r = 0, g = 0, b = 0;
  for (const [x, y] of [[2, 2], [3, 2], [2, 3], [3, 3]]) { const i = at(x, y); r += px[i]; g += px[i + 1]; b += px[i + 2]; }
  const hex = (v: number) => Math.round(v / 4).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/** The crest's main colour, for the team's name on the scoreboard: the most common strong colour, ignoring white, black and greys. */
export function dominantColor(px: Uint8ClampedArray, w: number, h: number): string {
  const counts = new Map<number, { n: number; r: number; g: number; b: number }>();
  const step = Math.max(1, Math.floor(Math.sqrt((w * h) / 40000)));          // sample about 40,000 pixels
  for (let y = 0; y < h; y += step) for (let x = 0; x < w; x += step) {
    const i = (y * w + x) * 4, r = px[i], g = px[i + 1], b = px[i + 2];
    if (px[i + 3] < 128) continue;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    if (max < 50 || min > 215 || max - min < 45) continue;                   // black, white or grey
    const key = ((r >> 5) << 6) | ((g >> 5) << 3) | (b >> 5);
    const c = counts.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    c.n++; c.r += r; c.g += g; c.b += b; counts.set(key, c);
  }
  let best: { n: number; r: number; g: number; b: number } | null = null;
  for (const c of counts.values()) if (!best || c.n > best.n) best = c;
  if (!best) return '#cccccc';
  const hex = (v: number) => Math.round(v / best!.n).toString(16).padStart(2, '0');
  return `#${hex(best.r)}${hex(best.g)}${hex(best.b)}`;
}
